/**
 * Soft Response sync bus — Soft≠Live.
 * Stamps which Soft queue task a Soft dispatch is bound to, then fans out to
 * Queue · People · Assign · Map Soft focus. Never writes Live APIs.
 * Map Soft focus is localhost-only Soft invent Soft production toast sticky.
 */
import {
  softQueueClone,
  softPeopleClone,
  softDevicesClone,
} from "./response-soft-demo.js";
import { isLocalHost } from "./pathseal.js";

export const SOFT_SYNC_EVENT = "croc-soft-response-sync";
export const SOFT_TASK_MAP_EVENT = "croc-soft-task-dispatch";
export const SOFT_SYNC_STORAGE = "croc.soft.response.sync";
export const SOFT_MAP_FOCUS_KEY = "croc.soft.task.focus";

const OPEN = new Set(["pending", "accepted", "en_route", "on_scene"]);

/** @type {null | { queue: any[], people: any[], assignments: Record<string, any>, revisedAt: number, soft_demo: boolean }} */
let _state = null;

function seedState() {
  return {
    queue: softQueueClone(),
    people: softPeopleClone(),
    assignments: {},
    revisedAt: 0,
    soft_demo: true,
  };
}

function loadState() {
  if (_state) return _state;
  try {
    const raw = sessionStorage.getItem(SOFT_SYNC_STORAGE);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.queue) && Array.isArray(parsed.people)) {
        _state = {
          queue: parsed.queue.map((r) => ({ ...r })),
          people: parsed.people.map((r) => ({ ...r })),
          assignments: parsed.assignments && typeof parsed.assignments === "object"
            ? { ...parsed.assignments }
            : {},
          revisedAt: Number(parsed.revisedAt) || 0,
          soft_demo: true,
        };
        return _state;
      }
    }
  } catch {
    /* ignore */
  }
  _state = seedState();
  return _state;
}

function persist(next) {
  _state = next;
  try {
    sessionStorage.setItem(SOFT_SYNC_STORAGE, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(SOFT_SYNC_EVENT, { detail: cloneSnap(next) }));
  } catch {
    /* ignore */
  }
}

function cloneSnap(st) {
  return {
    queue: (st.queue || []).map((r) => ({ ...r })),
    people: (st.people || []).map((r) => ({ ...r })),
    assignments: { ...(st.assignments || {}) },
    revisedAt: st.revisedAt || 0,
    soft_demo: true,
  };
}

export function softSyncSnapshot() {
  return cloneSnap(loadState());
}

export function softSyncReset() {
  persist(seedState());
  clearSoftMapFocus();
  return softSyncSnapshot();
}

/** Pure: pick highest-risk open Soft queue row for a device. */
export function softQueueTaskForDevice(deviceId, queueRows) {
  const id = String(deviceId || "").trim();
  if (!id) return null;
  const rows = Array.isArray(queueRows) ? queueRows : loadState().queue;
  const open = rows.filter(
    (r) => String(r.dev || "") === id && OPEN.has(String(r.status || "")),
  );
  if (!open.length) return null;
  open.sort((a, b) => (Number(b.risk) || 0) - (Number(a.risk) || 0));
  return { ...open[0] };
}

function adhocIds(deviceId) {
  const slug = String(deviceId || "x").replace(/[^a-zA-Z0-9_-]+/g, "-").slice(0, 48);
  return {
    queueId: "soft-demo-adhoc-" + slug,
    incident: "soft-inc-adhoc-" + slug,
  };
}

/** Ensure a Soft queue task exists for device (open job or Soft ad-hoc). */
export function softEnsureTaskForDevice(deviceId) {
  const st = loadState();
  const hit = softQueueTaskForDevice(deviceId, st.queue);
  if (hit) return { task: hit, created: false };
  const devices = softDevicesClone();
  const d = devices.find((x) => String(x.device_id || x.id || "") === String(deviceId));
  const label = String((d && (d.display_label || d.label)) || deviceId || "Soft device");
  const zone = String(label.split("·")[0] || label).trim() || "Soft zone";
  const { queueId, incident } = adhocIds(deviceId);
  const existing = st.queue.find((r) => r.id === queueId);
  if (existing) return { task: { ...existing }, created: false };
  const task = {
    id: queueId,
    status: "pending",
    who: "—",
    callsign: "UNASSIGNED",
    channel: "CH-SOFT",
    zone,
    dev: String(deviceId || ""),
    devLabel: label,
    reason: "Soft ad-hoc · stamped from dispatch",
    aiNote: "Created Soft · not Live",
    risk: 40,
    etaMin: 8,
    ageSec: 0,
    km: "—",
    opened: "—",
    correlation: "SOFT-ADHOC",
    incident,
    resolution: "",
    soft: true,
    soft_demo: true,
  };
  return { task, created: true };
}

function emitMapHandoff(detail) {
  const payload = {
    device: String(detail.deviceId || ""),
    queueId: String(detail.queueId || ""),
    incident: String(detail.incident || ""),
    zone: String(detail.zone || ""),
    who: String(detail.who || ""),
    taskLabel: String(detail.taskLabel || ""),
    soft_demo: true,
    from: "soft_task_dispatch",
    at: Date.now(),
  };
  // Soft≠Live: Soft map focus never sticks on production Soft invent Soft toast.
  if (!isLocalHost()) {
    clearSoftMapFocus();
    return payload;
  }
  try {
    sessionStorage.setItem(SOFT_MAP_FOCUS_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(SOFT_TASK_MAP_EVENT, { detail: payload }));
  } catch {
    /* ignore */
  }
  return payload;
}

/** Drop sticky Soft map focus Soft invent Soft remount toast / Soft plant pin. */
export function clearSoftMapFocus() {
  try {
    sessionStorage.removeItem(SOFT_MAP_FOCUS_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Stamp Soft units onto the Soft queue task for deviceId; sync People + Map handoff.
 * @param {{ deviceId: string, units: Array<{id:string,label?:string,kind?:string}>, source?: string }} opts
 */
export function softStampDispatch(opts) {
  const deviceId = String((opts && opts.deviceId) || "").trim();
  const units = Array.isArray(opts && opts.units) ? opts.units.filter((u) => u && u.id) : [];
  if (!deviceId || !units.length) {
    return { ok: false, reason: "missing_device_or_units" };
  }

  const st = loadState();
  const ensured = softEnsureTaskForDevice(deviceId);
  let queue = st.queue.map((r) => ({ ...r }));
  if (ensured.created) {
    queue = [ensured.task, ...queue.filter((r) => r.id !== ensured.task.id)];
  }
  const taskId = ensured.task.id;
  const names = units.map((u) => String(u.label || u.id)).filter(Boolean);
  const who = names.join(", ");
  const primary = units[0];
  const callsign = units.length === 1
    ? String(primary.label || primary.id)
    : (units.length + " Soft units");

  queue = queue.map((r) => {
    if (r.id !== taskId) return r;
    const nextStatus = r.status === "pending" || r.status === "accepted"
      ? "en_route"
      : r.status;
    return {
      ...r,
      who,
      callsign,
      status: nextStatus,
      aiNote: "Soft stamped · " + who + " → " + (r.devLabel || r.dev)
        + " · task " + r.id,
      soft_demo: true,
      soft: true,
    };
  });

  const task = queue.find((r) => r.id === taskId) || ensured.task;
  const taskLabel = String(task.zone || task.devLabel || task.id);
  const assignmentSay = taskLabel + " · " + task.id + " · Soft";

  const assignments = { ...(st.assignments || {}) };
  const people = st.people.map((p) => {
    const uid = String(p.user || p.id || "");
    const hit = units.find((u) => String(u.id) === uid);
    if (!hit) return { ...p };
    assignments[uid] = {
      queueId: task.id,
      incident: task.incident,
      deviceId,
      taskLabel,
      at: Date.now(),
      soft_demo: true,
    };
    return {
      ...p,
      assignmentSay,
      soft_demo: true,
    };
  });

  const next = {
    queue,
    people,
    assignments,
    revisedAt: Date.now(),
    soft_demo: true,
  };
  persist(next);

  const mapPayload = emitMapHandoff({
    deviceId,
    queueId: task.id,
    incident: task.incident,
    zone: task.zone,
    who,
    taskLabel,
  });

  return {
    ok: true,
    created: !!ensured.created,
    queueId: task.id,
    incident: task.incident,
    deviceId,
    taskLabel,
    who,
    status: task.status,
    source: String((opts && opts.source) || "soft"),
    map: mapPayload,
    soft_demo: true,
  };
}

export function softQueueRowsOf() {
  return softSyncSnapshot().queue;
}

export function softPeopleRowsOf() {
  return softSyncSnapshot().people;
}

/** Resources People register save — Soft≠Live. Updates Soft people shared with Response Soft roster. Never Live POST. */
export function softPeopleRegisterSave(username, patch) {
  const uid = String(username || "").trim();
  if (!uid || !patch || typeof patch !== "object") return softSyncSnapshot();
  const st = loadState();
  let hit = false;
  const people = st.people.map((p) => {
    if (String(p.user || p.id || "") !== uid) return { ...p };
    hit = true;
    const next = { ...p, soft_demo: true };
    for (const k of ["name", "role", "site", "phone", "phoneRegion", "phoneSay", "callsign", "notes", "crew"]) {
      if (patch[k] != null) next[k] = patch[k];
    }
    if (patch.phone != null) {
      const phone = String(patch.phone || "").trim();
      const region = String(patch.phoneRegion != null ? patch.phoneRegion : next.phoneRegion || "").trim();
      next.phone = phone;
      next.phoneRegion = region;
      next.phoneSay = phone ? (region ? `${phone} · ${region}` : phone) : "No phone on file";
    }
    return next;
  });
  if (!hit) {
    people.push({
      user: uid,
      name: String(patch.name || uid).trim() || uid,
      role: String(patch.role || "responder").trim() || "responder",
      site: String(patch.site || "—").trim() || "—",
      phone: String(patch.phone || "").trim(),
      phoneRegion: String(patch.phoneRegion || "").trim(),
      phoneSay: String(patch.phoneSay || "No phone on file"),
      callsign: String(patch.callsign || "").trim(),
      notes: String(patch.notes || "").trim(),
      crew: String(patch.crew || "").trim(),
      status: "active",
      on: false,
      ver: "soft-1",
      granted: "Soft · register",
      ret: 30,
      tier: "fine",
      hasFix: false,
      positionSay: "No live fix",
      assignmentSay: "Not on a Live job",
      soft_demo: true,
    });
  }
  persist({
    ...st,
    people,
    revisedAt: Date.now(),
    soft_demo: true,
  });
  return softSyncSnapshot();
}

export function softTaskChipForDevice(deviceId) {
  const task = softQueueTaskForDevice(deviceId);
  if (!task) return null;
  return {
    queueId: task.id,
    incident: task.incident,
    label: String(task.zone || task.devLabel || task.id),
    status: String(task.status || ""),
    who: String(task.who || "—"),
  };
}

/** Subscribe Soft sync fan-out; returns unsubscribe. */
export function softSyncSubscribe(cb) {
  if (typeof cb !== "function" || typeof window === "undefined") return () => {};
  const on = (ev) => {
    try {
      cb((ev && ev.detail) || softSyncSnapshot());
    } catch {
      /* ignore */
    }
  };
  window.addEventListener(SOFT_SYNC_EVENT, on);
  return () => window.removeEventListener(SOFT_SYNC_EVENT, on);
}

/** Soft Queue stage advance — mirrors into shared Soft catalog. */
export function softQueueAdvance(queueId, status) {
  const id = String(queueId || "").trim();
  const to = String(status || "").trim();
  if (!id || !to) return softSyncSnapshot();
  const st = loadState();
  const next = {
    ...st,
    queue: st.queue.map((r) => (r.id === id ? { ...r, status: to, soft_demo: true } : { ...r })),
    revisedAt: Date.now(),
    soft_demo: true,
  };
  persist(next);
  return softSyncSnapshot();
}
